import { useState, type FormEvent } from 'react';
import { useCatalogDiscovery } from '../hooks/useCatalogDiscovery';
import { useCredentials, useSaveBitrixStoreSetup } from '../hooks/useCredentials';
import { useTranslation } from '@shared/i18n';

export function BitrixCatalogSelector() {
  const { t } = useTranslation();
  const { data: catalogData, isLoading: discovering, refetch } = useCatalogDiscovery();
  const { data: credData } = useCredentials();
  const save = useSaveBitrixStoreSetup();

  const bitrixCred = credData?.credentials?.find((c) => c.marketplace === 'bitrix');

  const [catalogIblockId, setCatalogIblockId] = useState('');
  const [catalogGroupId, setCatalogGroupId] = useState('');

  const portalDomain = window.BX24?.getDomain() ?? '';

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    await save.mutateAsync({
      catalogIblockId,
      catalogGroupId,
    });
  };

  return (
    <div className="space-y-4">
      {portalDomain && (
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <span className="inline-block w-2 h-2 rounded-full bg-green-500"></span>
          {t('settings.portal_connected')}: <span className="font-medium">{portalDomain}</span>
        </div>
      )}

      {bitrixCred?.isValid && (
        <div className="text-sm rounded-lg p-3 bg-green-50 border border-green-200 text-green-700">
          {t('settings.config_saved')}
        </div>
      )}

      {discovering ? (
        <p className="text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <form onSubmit={handleSave} className="space-y-4">
          <div>
            <label htmlFor="catalog" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.select_catalog')}
            </label>
            <select
              id="catalog"
              value={catalogIblockId}
              onChange={(e) => setCatalogIblockId(e.target.value)}
              className="input"
            >
              <option value="">{t('settings.select_catalog')}</option>
              {catalogData?.catalogs.map((cat) => (
                <option key={cat.id} value={String(cat.iblockId)}>
                  {cat.name} (iblock: {cat.iblockId})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="priceType" className="block text-sm font-medium text-gray-700 mb-1">
              {t('settings.select_price_type')}
            </label>
            <select
              id="priceType"
              value={catalogGroupId}
              onChange={(e) => setCatalogGroupId(e.target.value)}
              className="input"
            >
              <option value="">{t('settings.select_price_type')}</option>
              {catalogData?.priceTypes.map((pt) => (
                <option key={pt.id} value={String(pt.id)}>
                  {pt.name} ({pt.xmlId})
                </option>
              ))}
            </select>
          </div>

          {save.isSuccess && save.data && (
            <div
              className={`text-sm rounded-lg p-3 ${
                save.data.isValid
                  ? 'bg-green-50 border border-green-200 text-green-700'
                  : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
              }`}
            >
              {save.data.message}
            </div>
          )}

          {save.error && (
            <div className="text-sm rounded-lg p-3 bg-red-50 border border-red-200 text-red-700">
              {save.error instanceof Error ? save.error.message : 'Save failed'}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              disabled={discovering}
              className="btn btn-outline"
            >
              {t('settings.redetect')}
            </button>
            <button type="submit" disabled={save.isPending} className="btn btn-primary">
              {save.isPending ? t('settings.saving') : t('settings.save_config')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
